import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { CalendarDays } from 'lucide-react';
import GlobalHeader from '@/components/GlobalHeader';
import DatesTabs from '@/components/dates/DatesTabs';
import PrintButton from '@/components/PrintButton';
import { toast } from 'sonner';
import APP_ICONS from '@/utils/appIcons';

import FamilyTab from '@/components/dates/FamilyTab';
import BirthdaysTab from '@/components/dates/BirthdaysTab';
import CommunityTab from '@/components/dates/CommunityTab';
import AppointmentsTab from '@/components/dates/AppointmentsTab';
import ReservationsTab from '@/components/dates/ReservationsTab';
import MembershipsTab from '@/components/dates/MembershipsTab';

// Important Dates module pastel theme - Soft Lavender/Periwinkle
const DATES_THEME = {
  bgGradient: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 30%, #FDF2F8 60%, #FEF3F2 100%)',
  headerIconBg: '#E9D5FF',
  headerIconColor: 'text-purple-600',
  cardBg: 'rgba(255, 255, 255, 0.85)',
  cardBorder: '#E9D5FF',
  accentLight: '#F5F3FF',
  accentDark: '#7C3AED',
};

// Tab display names for print
const TAB_NAMES = {
  family: 'Shared Events',
  birthday: 'Birthdays',
  community: 'Community Events',
  appointment: 'Appointments',
  reservation: 'Reservations',
  membership: 'Memberships'
};

const DatesPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('family');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(async () => {
    setRefreshKey(prev => prev + 1);
    toast.success('Data refreshed');
  }, []);

  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'family':
        return <FamilyTab key={`family-${refreshKey}`} />;
      case 'birthday':
        return <BirthdaysTab key={`birthday-${refreshKey}`} />;
      case 'community':
        return <CommunityTab key={`community-${refreshKey}`} />;
      case 'appointment':
        return <AppointmentsTab key={`appointment-${refreshKey}`} />;
      case 'reservation':
        return <ReservationsTab key={`reservation-${refreshKey}`} />;
      case 'membership':
        return <MembershipsTab key={`membership-${refreshKey}`} />;
      default:
        return <FamilyTab key={`family-${refreshKey}`} />;
    }
  };

  return (
    <div 
      className="min-h-screen" 
      style={{ background: DATES_THEME.bgGradient }}
      data-testid="dates-page"
    >
      <GlobalHeader
        iconSrc={APP_ICONS.dates}
        iconBgColor={DATES_THEME.headerIconBg}
        iconColor={DATES_THEME.headerIconColor}
        title="Important Dates"
        subtitle="Remember what matters"
        onRefresh={handleRefresh}
      >
        <PrintButton title={`Important Dates - ${TAB_NAMES[activeTab]}`} />
      </GlobalHeader>

      {/* Print Header - Only visible when printing */}
      <div className="print-only print-header px-6 py-4">
        <div>
          <h1 className="print-title">Important Dates</h1>
          <p className="text-sm text-slate-600">{TAB_NAMES[activeTab]}</p>
        </div>
        <div className="print-date">
          Printed: {new Date().toLocaleDateString()}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-28">
        <Card 
          className="shadow-sm backdrop-blur-sm print-card"
          style={{ 
            backgroundColor: DATES_THEME.cardBg,
            borderColor: DATES_THEME.cardBorder 
          }}
        >
          <CardContent className="p-3 sm:p-6">
            <DatesTabs activeTab={activeTab} onTabChange={setActiveTab}>
              {renderTabContent()}
            </DatesTabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DatesPage;
